import React, { MouseEventHandler, useMemo } from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import EDPaper from '../../../BaseComponents/Paper/EDPaper';
import classNames from 'classnames';
import { useIsTouchDevice } from '../../BreakpointContext/BreakpointContextUtils';
import { useIsFloatingMenuVisible } from '../../FloatingBottomArea/FloatingMenu';
import {
  ZoomableImageNavigation,
  ZoomableImageHeader,
} from './ZoomableImageToolbar';
import { TransformComponent, useTransformContext } from 'react-zoom-pan-pinch';
import ZoomableImageRotationWrapper from './ZoomableImageRotationWrapper';

interface IProps {
  panning?: boolean;
  toolbar?: React.ReactNode;
  topToolbar?: React.ReactNode;
  centered?: boolean;
  setPaperRef?: (element: HTMLDivElement) => void;
  extraBottomPadding?: boolean;
  ignoreFloatingMenu?: boolean;
  disableNavigation?: boolean;
  itemCount: number;
  itemIndex: number;
  onNextClick: () => void;
  onPrevClick: () => void;
  navHeader: string;
  navHeaderEttGenus?: boolean;
  rotation: number;
  rotationReset: () => void;
  onContextMenu?: MouseEventHandler<HTMLDivElement>;
  isImage: boolean;
  useWheelToScroll: boolean;
  classes?: any;
}

const styles = (theme: Theme, _: any, classes: any) => ({
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    height: '100%',
    position: 'relative', // Behövs för att toolbaren har position: absolute
    [`&:hover .${classes.toolbarContainer}`]: {
      opacity: 1,
    },
    [`&:hover .${classes.topToolbarContainer}`]: {
      height: '30px',
      opacity: 0.5,
    },
  },
  toolbarContainer: {
    position: 'absolute',
    bottom: 10,
    right: 5,
    opacity: 0,
    transition: 'opacity 100ms ease',
    zIndex: 101,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    pointerEvents: 'none',
  },
  mobileToolbarContainer: {
    opacity: 1,
  },
  topToolbarContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: 'auto',
    transition: 'height 100ms ease opacity 100ms ease',
    height: 0,
    opacity: 0,
    zIndex: 100,
    backgroundColor: theme.palette.common.black,
  },
  mobileTopToolbarContainer: {
    opacity: 0.5,
    height: '30px',
  },
  img: {
    objectFit: 'contain',
    height: '100%',
  },
  paperContainer: {
    width: 'calc(100% - 2px)',
    overflow: 'hidden',
    background: theme.customProperties.ActivityCardPanel.rootLightBg,
    border: theme.customProperties.ActivityCardPanel.border,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  paperContainerPadding: {
    height: '100%',
  },
  paper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.customProperties.ActivityCardPanel.rootLightBg,
  },
  fullHeight: {
    '&&': {
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    },
  },
  hideTopToolbar: {
    height: 0,
    display: 'none',
  },
  hideToolbar: {
    display: 'none',
  },
  wrapper: {
    '&&': {
      width: '100%',
      height: '100%',
      cursor: 'grab',
    },
  },
  imageType: {
    '&&': {
      width: 'inherit',
    },
  },
});

const ZoomableImage: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = ({ setPaperRef, ...props }) => {
  const isTouchDevice = useIsTouchDevice();
  const isFloatingMenuVisible =
    useIsFloatingMenuVisible() && !props.ignoreFloatingMenu;
  const transformContext = useTransformContext();

  const onWheel: React.WheelEventHandler<HTMLDivElement> | undefined =
    props.useWheelToScroll
      ? event => {
          if (!transformContext.instance.isPanning && !event.altKey) {
            const scrollPosition =
              event.deltaY > 0
                ? -100 * (Math.abs(event.deltaY) / 100)
                : 100 * (Math.abs(event.deltaY) / 100);
            if (event.shiftKey) {
              transformContext.setTransform(
                transformContext.state.positionX + scrollPosition / 2,
                transformContext.state.positionY,
                transformContext.state.scale
              );
            } else {
              transformContext.setTransform(
                transformContext.state.positionX,
                transformContext.state.positionY + scrollPosition,
                transformContext.state.scale
              );
            }
          }

          if (event.altKey) {
            if (event.deltaY > 0) {
              transformContext.zoomOut(0.1);
            } else {
              transformContext.zoomIn(0.1);
            }
          }
        }
      : undefined;

  const panning = useMemo(() => props.panning, [props.panning]);

  return (
    <div className={props.classes.container} onWheel={onWheel}>
      {props.toolbar && (
        <div
          className={classNames(props.classes.toolbarContainer, {
            [props.classes.hideToolbar]: panning && !isTouchDevice,
            [props.classes.mobileToolbarContainer]: isTouchDevice,
          })}
          style={isFloatingMenuVisible ? { right: 0 } : undefined}
        >
          {props.toolbar}
        </div>
      )}
      {!props.disableNavigation && props.itemCount > 1 && (
        <>
          <ZoomableImageNavigation
            direction="prev"
            imageCount={props.itemCount}
            imageIndex={props.itemIndex}
            onClick={() => {
              props.rotationReset();
              props.onPrevClick();
            }}
            header={props.navHeader}
            headerEttGenus={props.navHeaderEttGenus}
          />
          <ZoomableImageNavigation
            direction="next"
            imageCount={props.itemCount}
            imageIndex={props.itemIndex}
            onClick={() => {
              props.rotationReset();
              props.onNextClick();
            }}
            header={props.navHeader}
            headerEttGenus={props.navHeaderEttGenus}
          />
          <ZoomableImageHeader
            count={props.itemCount}
            index={props.itemIndex}
            header={props.navHeader}
          />
        </>
      )}
      <div
        ref={setPaperRef}
        className={classNames(props.classes.paperContainer, {
          [props.classes.paperContainerPadding]: props.extraBottomPadding,
        })}
        onContextMenu={props.onContextMenu}
      >
        <EDPaper className={props.classes.paper}>
          <TransformComponent
            wrapperClass={props.classes.wrapper}
            contentClass={classNames({
              [props.classes.fullHeight]: !props.centered,
              [props.classes.imageType]: props.isImage,
            })}
          >
            <ZoomableImageRotationWrapper rotation={props.rotation}>
              {props.children}
            </ZoomableImageRotationWrapper>
          </TransformComponent>
        </EDPaper>
      </div>
    </div>
  );
};

export default withStyles(ZoomableImage, styles, {
  uniqId: '30be0645-dd1b-47ea-960d-398fc12d7e0a',
});
